/* The batch page (WP-1b.3, BLOCK-1B-BULK-DESIGN §1, §7). Logical properties only. */

/* Step content beside the batch rail (20rem, sticky, never collapses on desktop). */
.body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 20rem;
  gap: var(--space-5);
  align-items: start;
}

.block {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-inline-size: 0;
}

.block + .block {
  padding-block-start: var(--space-4);
  border-block-start: var(--border-width) solid var(--color-rule);
}

.blockTitle {
  margin: 0;
  font-size: var(--text-lg);
}

.legend {
  margin: 0;
  color: var(--color-heading);
  font-weight: var(--weight-semibold);
}

.lead {
  margin: 0;
  color: var(--color-text-muted);
}

.addField {
  max-inline-size: 32rem;
}

.option {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.mono {
  font-family: var(--font-mono);
  unicode-bidi: isolate;
  white-space: nowrap;
}

.numberLink {
  color: var(--color-link);
  font-weight: var(--weight-semibold);
  text-decoration: none;
  unicode-bidi: isolate;
}

.numberLink:hover {
  text-decoration: underline;
}

.criteria {
  margin: 0;
  color: var(--color-text);
}

/* Text links and text buttons: underlined text, no fill, no border. */
.textLink,
.textButton {
  color: var(--color-link);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.textButton {
  padding-inline: var(--space-1);
  border: 0;
  background: none;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.badges {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.willRun {
  color: var(--status-valid);
  font-weight: var(--weight-semibold);
}

.willSkip {
  color: var(--status-pending-text);
}

.summary {
  margin: 0;
}

.following {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.progress {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-3) var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-info-bg);
  color: var(--color-info-text);
}

.progressNote {
  font-size: var(--text-sm);
}

/* RIB-09: an indeterminate bar while the server runs; static (a still segment) under reduced
   motion, where the words alone carry the state. */
.busy {
  position: relative;
  display: block;
  inline-size: 100%;
  max-inline-size: 18rem;
  block-size: 0.25rem;
  overflow: hidden;
  border-radius: var(--radius-sm, 2px);
  background: color-mix(in srgb, currentColor 18%, transparent);
}

.busyFill {
  position: absolute;
  inset-block: 0;
  inline-size: 35%;
  border-radius: inherit;
  background: currentColor;
  animation: batch-busy 1.2s ease-in-out infinite alternate;
}

/* The segment travels inside the bar and back, so any moment shows it whole. */
@keyframes batch-busy {
  from {
    inset-inline-start: 0;
  }
  to {
    inset-inline-start: 65%;
  }
}

/* RIB-06: the undo bar rides on the step bar, just above it, at every width. */
.barLayer {
  position: absolute;
  inset-inline: 0;
  inset-block-end: calc(100% + var(--space-3));
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.barLayer > * {
  pointer-events: auto;
}

:global([dir]) .barLayer > .undoOnBar {
  position: static;
  inset: auto;
  transform: none;
  max-inline-size: calc(100% - 2 * var(--space-4));
}

/* Outcomes: an icon and text, never colour alone. */
.outcomeDone,
.outcomeWaiting,
.outcomeSkipped,
.outcomeFailed,
.outcomeUnconfirmed {
  display: inline-flex;
  align-items: baseline;
  gap: var(--space-1);
}

/* The icon (or the live dot) keeps its size and sits on the first line of the words. */
.outcomeDone > svg,
.outcomeSkipped > svg,
.outcomeFailed > svg,
.outcomeUnconfirmed > svg,
.outcomeWaiting > .liveDot {
  flex: none;
  align-self: center;
}

.outcomeDone {
  color: var(--status-valid);
}

.outcomeWaiting {
  color: var(--status-pending-text);
}

.outcomeSkipped {
  color: var(--color-text-muted);
}

.outcomeFailed {
  color: var(--color-danger-text);
}

.outcomeUnconfirmed {
  color: var(--status-pending-text);
}

.liveDot {
  inline-size: 0.5rem;
  block-size: 0.5rem;
  border-radius: 50%;
  background: var(--status-pending);
}

/* ------------------------------------------------------------------ the batch rail */
/* The named list scrolls on its own, so the room left and «إفراغ الحزمة» stay in view below it. */
.rail {
  position: sticky;
  inset-block-start: calc(var(--app-header-size) + var(--space-4));
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}

.railTitle {
  margin: 0;
  font-size: var(--text-md);
  outline: none;
}

.railTitle:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}

.railTask {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.railList {
  display: flex;
  flex-direction: column;
  max-block-size: 28rem;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  /* RIB-08: scrolled rows rest on an item's start, so no row is left cut through its button. */
  scroll-snap-type: y proximity;
}

.railList > li {
  scroll-snap-align: start;
}

/* RIB-08: while more items lie below, the list's foot fades out: a cue that it scrolls, and a
   partly visible row (its «أخرِج» included) never shows as a crisp fragment. */
.railList[data-more='true'] {
  mask-image: linear-gradient(to bottom, #000 calc(100% - 4rem), transparent);
}

.railItem {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'number out'
    'buyer out'
    'amount out';
  column-gap: var(--space-2);
  padding-block: var(--space-2);
  border-block-end: var(--border-width) solid var(--color-rule);
}

.railNumber {
  grid-area: number;
  font-weight: var(--weight-semibold);
  unicode-bidi: isolate;
  justify-self: start;
}

.railBuyer {
  grid-area: buyer;
  min-inline-size: 0;
  overflow-wrap: anywhere;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.railAmount {
  grid-area: amount;
  font-size: var(--text-sm);
}

.railItem > :last-child {
  grid-area: out;
  align-self: center;
}

.railEmpty {
  margin: 0;
  color: var(--color-text-muted);
}

/* The room left and «إفراغ الحزمة» each on a line of their own (AC-B4). */
.room {
  display: block;
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.clearLine {
  display: block;
}

/* Below 64rem: the rail opens in place under the step list. */
.railDisclosure {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-inline-size: 0;
}

.railToggle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  inline-size: 100%;
  min-block-size: 2.75rem;
  padding: var(--space-2) var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-weight: var(--weight-semibold);
  text-align: start;
  cursor: pointer;
}

.railToggle[data-focus-visible] {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}

.railChevron {
  margin-inline-start: auto;
  transition: transform var(--duration-fast) var(--ease-out);
}

.railToggle[aria-expanded='true'] .railChevron {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .railChevron {
    transition: none;
  }
  .busyFill {
    inset-inline-start: 0;
    animation: none;
  }
}

@media (max-width: 64rem) {
  .body {
    grid-template-columns: minmax(0, 1fr);
  }
  .rail {
    position: static;
  }
  .railList {
    max-block-size: none;
    overflow: visible;
  }
  .railList[data-more='true'] {
    mask-image: none;
  }
  /* RIB-07: under the toggle «الحزمة» the rail's own heading is not shown twice; it stays the
     rail's label and its focus fallback. */
  .railTitle {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}

/* Phones (390×844): compact step circles (44 px) with the current step's name only. */
@media (max-width: 40rem) {
  .steps button {
    min-block-size: 2.75rem;
  }
  .steps button:not([aria-current='step']) > span:nth-child(2) {
    display: none;
  }
  .steps button:not([aria-current='step']) {
    justify-content: center;
  }
  .railItem :global(button) {
    min-block-size: 2.75rem;
    min-inline-size: 2.75rem;
  }
  .actions > * {
    min-block-size: 2.75rem;
  }
}
